import React, { useState, useEffect, useRef } from 'react';
import { X, Paperclip, PlayCircle, File as FileIcon, ChevronRight } from 'react-feather';
import { XIcon } from './XIcon';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { integrationsAPI } from '../services/integrationsAPI';
import { mediaUploadService } from '../services/mediaUploadService';
import { userAPI, familyAPI } from '../services/api';
import { useConfirm } from '../hooks/useConfirm';
import Modal, { ModalHandle } from './Modal';
import ErrorState from './ErrorState';
import YouTubeVideoPickerModal from './YouTubeVideoPickerModal';
import { useResponsive } from '../hooks/useResponsive';
import { getVideoDurationSeconds, xVideoDurationError } from '../utils/videoDuration';
import { TextArea } from './ui';

interface Coach {
  coach_name: string;
  twitter_handle: string;
  title?: string;
  school?: string;
  schoolLogo?: string;
  coach_id?: string;
  school_id?: string;
}

interface BulkTweetCoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fires after onClose when the post went up. */
  onPosted?: () => void;
  coaches: Coach[];
  onRemoveCoach?: (twitterHandle: string) => void;
}

interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'document';
}

const BulkTweetCoachModal: React.FC<BulkTweetCoachModalProps> = ({
  isOpen,
  onClose,
  onPosted,
  coaches,
  onRemoveCoach,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [athleteId, setAthleteId] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [tweetText, setTweetText] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<MediaFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [youtubePickerVisible, setYoutubePickerVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Guards the tweet prefill so it runs only on the open transition, not on
  // every `coaches` identity change (which wiped drafted edits) (#192).
  const didPrefillRef = useRef(false);

  // Effective X post limit for the connected account (280 basic / 25000 Premium),
  // resolved from the integration service. Seeded at the basic limit so an
  // unresolved lookup can never let a basic account overrun 280 (#190, KGH-785).
  const [characterLimit, setCharacterLimit] = useState(280);
  // X Premium accounts may post longer videos; drives the duration check at pick time.
  const [isPremium, setIsPremium] = useState(false);
  const characterCount = tweetText.length;

  const { ConfirmComponent: RemoveCoachConfirm, confirm: confirmRemoveCoach } = useConfirm({
    title: 'Remove Coach',
    message: 'Are you sure you want to remove this coach from the tweet?',
    confirmText: 'Remove',
    cancelText: 'Cancel',
    variant: 'warning',
  });

  // Normalize X handle
  const normalizeTwitterHandle = (handle: string): string => {
    if (!handle) return '';
    let normalized = handle.trim().replace(/\s+/g, '').replace(/@+/g, '');
    if (normalized) {
      normalized = `@${normalized}`;
    }
    return normalized;
  };

  // Initialize the tweet with all coach handles — but ONLY on the open
  // transition. The old effect keyed on `coaches`, whose array identity changes
  // on every parent render, so it re-ran and clobbered the drafted tweet (and
  // wiped everything the moment a coach was removed) (#192).
  useEffect(() => {
    if (!isOpen) {
      didPrefillRef.current = false;
      return;
    }
    if (!didPrefillRef.current && coaches.length > 0) {
      const prefix = 'Check this out! ';
      const allHandles = coaches
        .map(coach => normalizeTwitterHandle(coach.twitter_handle))
        .map(handle => handle.replace('@', ''))
        .filter(handle => handle)
        .map(handle => `@${handle}`);
      // Only prefill as many @handles as fit within the 280-char limit so the
      // seed never lands the composer over the cap (maxLength silently truncates).
      const included: string[] = [];
      for (const h of allHandles) {
        if (`${prefix}${[...included, h].join(' ')} `.length > characterLimit) break;
        included.push(h);
      }
      setTweetText(`${prefix}${included.join(' ')} `);
      didPrefillRef.current = true;
    }
  }, [isOpen, coaches]);

  // Resolve the athlete and check the X connection in one pass. The `finally`
  // guarantees the "Checking connection…" flag is always cleared, even when
  // athlete resolution fails (#183).
  const loadAthleteAndConnect = async () => {
    if (!sessionToken) return;

    setIsCheckingConnection(true);
    setLoadError(false);
    try {
      const userProfile = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);

      let resolvedAthleteId = '';
      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          member => member.id === userProfile.id
        );
        if (familyMember?.athlete_profile_id) {
          resolvedAthleteId = familyMember.athlete_profile_id;
        }
      }

      if (!resolvedAthleteId) {
        throw new Error('Could not find your athlete profile.');
      }

      setAthleteId(resolvedAthleteId);
      const connectionStatus = await integrationsAPI.getConnectionStatus(resolvedAthleteId);
      setIsConnected(connectionStatus.twitter);
      // Premium/PremiumPlus accounts get 25000 chars — resolve the real limit so
      // the composer doesn't cap a paid X account at 280. Fail-safe stays 280.
      if (connectionStatus.twitter) {
        integrationsAPI.getXCharacterLimit(resolvedAthleteId)
          .then(info => {
            setCharacterLimit(info.character_limit);
            setIsPremium(info.is_premium);
          })
          .catch(() => {
            setCharacterLimit(280);
            setIsPremium(false);
          });
      }
    } catch (error) {
      console.error('Error loading athlete data for bulk tweet:', error);
      setIsConnected(false);
      setLoadError(true);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Load athlete ID and check X connection when the modal opens.
  useEffect(() => {
    if (isOpen && sessionToken) {
      loadAthleteAndConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionToken]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    // Snapshot synchronously — the input is reset below, after async checks.
    const picked = Array.from(files);

    const newMediaFiles: MediaFile[] = [];
    
    for (const file of picked) {
      // Check if we've reached the limit
      if (attachedMedia.length + newMediaFiles.length >= 4) {
        toast.warning('Maximum 4 files allowed');
        break;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.warning(`${file.name} is too large. Maximum file size is 10MB.`);
        continue;
      }

      // Determine file type
      let type: 'image' | 'video' | 'document' = 'document';
      if (file.type.startsWith('image/')) {
        type = 'image';
      } else if (file.type.startsWith('video/')) {
        type = 'video';
      }

      // X rejects over-length videos only AFTER the upload, with no usable
      // error (KGH-785). Check here, where the duration is readable.
      if (type === 'video') {
        const duration = await getVideoDurationSeconds(file);
        const tooLong = duration != null ? xVideoDurationError(duration, isPremium) : null;
        if (tooLong) {
          toast.warning(tooLong);
          continue;
        }
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);

      newMediaFiles.push({ file, preview, type });
    }

    // X accepts either ONE video (with nothing else) or up to FOUR images.
    const combined = [...attachedMedia, ...newMediaFiles];
    const firstVideo = combined.find((m) => m.type === 'video');
    if (firstVideo && combined.length > 1) {
      toast.warning('X allows a single video with no other media, or up to 4 images. Keeping the first video.');
      combined.filter((m) => m !== firstVideo).forEach((m) => URL.revokeObjectURL(m.preview));
      setAttachedMedia([firstVideo]);
    } else {
      setAttachedMedia(combined);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMediaFile = (index: number) => {
    const newMedia = [...attachedMedia];
    URL.revokeObjectURL(newMedia[index].preview);
    newMedia.splice(index, 1);
    setAttachedMedia(newMedia);
  };

  const handleRemoveCoach = async (twitterHandle: string, coachName: string) => {
    const confirmed = await confirmRemoveCoach();
    if (confirmed && onRemoveCoach) {
      onRemoveCoach(twitterHandle);
      
      if (coaches.length <= 1) {
        toast.info('All coaches removed from tweet');
        handleClose();
        return;
      }
      
      // Update tweet text to remove the handle
      const handleToRemove = normalizeTwitterHandle(twitterHandle);
      const updatedText = tweetText.replace(new RegExp(`${handleToRemove}\\s*`, 'g'), '');
      setTweetText(updatedText);
    }
  };

  const uploadMediaFiles = async (): Promise<string[]> => {
    if (attachedMedia.length === 0) return [];

    if (!athleteId || !sessionToken) {
      throw new Error('Missing athlete ID or session token');
    }

    setIsUploading(true);

    try {
      const uploadResults = await mediaUploadService.uploadMultipleMedia(
        attachedMedia,
        athleteId,
        sessionToken,
        (index, progress) => {
          console.log(`📊 Upload progress for file ${index + 1}: ${progress.percentage}%`);
          const overallProgress = Math.round(
            ((index + (progress.percentage / 100)) / attachedMedia.length) * 100
          );
          setUploadProgress(overallProgress);
        }
      );

      const failedUploads = uploadResults.filter(result => !result.success);
      if (failedUploads.length > 0) {
        console.error('❌ Some files failed to upload:', failedUploads);
        throw new Error('Some files failed to upload');
      }

      const mediaUrls = uploadResults
        .filter(result => result.success && result.fileUrl)
        .map(result => result.fileUrl!);

      console.log('✅ All files uploaded successfully:', mediaUrls);
      return mediaUrls;

    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePostTweet = async () => {
    if (!athleteId || !tweetText.trim()) return;

    setIsPosting(true);

    try {
      let mediaUrls: string[] = [];

      if (attachedMedia.length > 0) {
        mediaUrls = await uploadMediaFiles();
      }

      // Extract coach_ids and school_ids from coaches array
      const coachIds = coaches
        .map(c => c.coach_id)
        .filter((id): id is string => id !== undefined);
      const schoolIds = coaches
        .map(c => c.school_id)
        .filter((id): id is string => id !== undefined);

      await integrationsAPI.postToTwitter(athleteId, {
        text: tweetText.trim(),
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
        mentions: (coachIds.length > 0 || schoolIds.length > 0) ? {
          coach_ids: coachIds.length > 0 ? coachIds : undefined,
          school_ids: schoolIds.length > 0 ? schoolIds : undefined
        } : undefined
      });

      toast.success('Posted to X.');
      handleClose();
      onPosted?.();
    } catch (error) {
      console.error('Error posting tweet:', error);
      const message = error instanceof Error ? error.message : 'Failed to post tweet. Please try again.';
      toast.error(message);
    } finally {
      setIsPosting(false);
    }
  };

  const handleYouTubeVideosSelected = (videoLinks: string[]) => {
    const currentText = tweetText;
    const videosText = videoLinks.join('\n');
    const newText = currentText ? `${currentText}\n\n${videosText}` : videosText;
    setTweetText(newText);
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    setTweetText('');
    setIsPosting(false);
    attachedMedia.forEach(media => URL.revokeObjectURL(media.preview));
    setAttachedMedia([]);
    setUploadProgress(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <RemoveCoachConfirm />
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        ref={composerModalRef}
        isDirty={tweetText.trim().length > 0}
        disableClose={isPosting || isUploading || youtubePickerVisible}
        ariaLabel="Tag Coaches on X"
        zIndex={1002}
        contentStyle={{
          background: 'white',
          borderRadius: '16px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
          {/* Header */}
          <div
            style={{
              padding: isMobile ? '16px' : '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#151133',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: 'white',
              }}
            >
              Tag Coaches on X
            </h2>
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isPosting || isUploading}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: (isPosting || isUploading) ? 'not-allowed' : 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'background 0.2s',
                opacity: (isPosting || isUploading) ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <X size={24} color="white" />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: '#F9FAFB' }}>
            {isCheckingConnection ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 32px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    background: '#F3F4F6',
                    borderRadius: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  }}
                >
                  <XIcon size={32} />
                </div>
                <p style={{ fontSize: '16px', color: '#6B7280', fontWeight: '600', margin: 0 }}>
                  Checking X connection...
                </p>
              </div>
            ) : loadError ? (
              /* Failed to load account */
              <ErrorState
                message="We couldn't load your account to post these tweets. Please try again."
                onRetry={loadAthleteAndConnect}
                style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}
              />
            ) : !isConnected ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 32px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    background: '#F3F4F6',
                    borderRadius: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <XIcon size={48} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#000000', marginBottom: '12px', margin: '0 0 12px 0' }}>
                  X Account Not Connected
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5', margin: '0 0 16px 0' }}>
                  To tag coaches and post tweets, you need to connect your X account first.
                </p>
                <p style={{ fontSize: '14px', color: '#F9550D', fontWeight: '600', lineHeight: '1.5', margin: 0 }}>
                  Go to Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings to connect your social media accounts.
                </p>
              </div>
            ) : (
              <>
                {/* Coaches Summary Card */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    marginBottom: '16px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '25px',
                      background: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <XIcon size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#000000', marginBottom: '4px' }}>
                      Tagging {coaches.length} Coach{coaches.length > 1 ? 'es' : ''}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#000000', wordBreak: 'break-word' }}>
                      {coaches.map(c => normalizeTwitterHandle(c.twitter_handle)).join(' ')}
                    </div>
                  </div>
                </div>

                {/* Individual Coach Cards */}
                {coaches.map((coach, index) => (
                  <div
                    key={`${coach.twitter_handle}-${index}`}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '12px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    {coach.schoolLogo ? (
                      <img
                        src={coach.schoolLogo}
                        alt={coach.school}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                        {coach.coach_name}
                      </div>
                      {coach.title && (
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>
                          {coach.title}
                        </div>
                      )}
                      {coach.school && (
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>
                          {coach.school}
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#000000', marginTop: '2px', fontWeight: '600' }}>
                        {normalizeTwitterHandle(coach.twitter_handle)}
                      </div>
                    </div>
                    {onRemoveCoach && coaches.length > 1 && (
                      <button
                        onClick={() => handleRemoveCoach(coach.twitter_handle, coach.coach_name)}
                        style={{
                          padding: '8px',
                          background: '#FEE2E2',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#EF4444',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#FECACA';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#FEE2E2';
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}

                {/* Tweet Composer */}
                <div
                  style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    marginTop: '24px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '12px',
                    }}
                  >
                    Compose your post:
                  </label>
                  <div
                    style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '16px',
                    }}
                  >
                    {/* The composer shell owns the border and holds the character
                        counter below the box, so the field inside stays borderless —
                        the primitive is here for the disabled, placeholder and focus
                        states, and for dropping the inline `outline: none` that left
                        a keyboard user with only the parent's border swap. */}
                    <TextArea
                      value={tweetText}
                      onChange={(e) => setTweetText(e.target.value)}
                      placeholder="What would you like to say?"
                      aria-label="Post text"
                      maxLength={characterLimit}
                      style={{
                        minHeight: '120px',
                        maxHeight: '200px',
                        padding: '16px',
                        border: 'none',
                        borderRadius: 0,
                        fontSize: '16px',
                        resize: 'none',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.parentElement!.style.borderColor = '#000000';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.parentElement!.style.borderColor = '#E5E7EB';
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        padding: '0 16px 12px 16px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: characterCount > characterLimit - 20 ? '#EF4444' : '#6B7280',
                        }}
                      >
                        {characterCount}/{characterLimit}
                      </div>
                    </div>
                  </div>

                  {/* YouTube Button */}
                  <button
                    onClick={() => setYoutubePickerVisible(true)}
                    disabled={isPosting || isUploading}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: '#FFF5F5',
                      border: '2px solid #FF0000',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#FF0000',
                      cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      opacity: isPosting || isUploading ? 0.5 : 1,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isPosting && !isUploading) e.currentTarget.style.background = '#FFEBEB';
                    }}
                    onMouseLeave={(e) => {
                      if (!isPosting && !isUploading) e.currentTarget.style.background = '#FFF5F5';
                    }}
                  >
                    <PlayCircle size={20} color="#FF0000" />
                    YouTube Videos
                  </button>

                  {/* Media Section */}
                  <div style={{ marginTop: '8px' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachedMedia.length >= 4 || isPosting || isUploading}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: '#F8FAFC',
                        border: '2px dashed #F9550D',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#F9550D',
                        cursor: attachedMedia.length >= 4 || isPosting || isUploading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: attachedMedia.length >= 4 || isPosting || isUploading ? 0.5 : 1,
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (attachedMedia.length < 4 && !isPosting && !isUploading) {
                          e.currentTarget.style.background = '#FFF5F0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (attachedMedia.length < 4 && !isPosting && !isUploading) {
                          e.currentTarget.style.background = '#F8FAFC';
                        }
                      }}
                    >
                      <Paperclip size={16} />
                      Add File{attachedMedia.length > 0 ? ` (${attachedMedia.length}/4)` : ''}
                    </button>

                    <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center', marginTop: '8px', lineHeight: '1.4' }}>
                      Maximum file size: 10MB.{'\n'}
                      Supported formats: PDF, DOC, images, and more.
                    </div>

                    {attachedMedia.length > 0 && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '16px', overflowX: 'auto', paddingBottom: '8px' }}>
                        {attachedMedia.map((media, index) => (
                          <div
                            key={index}
                            style={{
                              position: 'relative',
                              flexShrink: 0,
                            }}
                          >
                            {media.type === 'document' ? (
                              <div
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  borderRadius: '8px',
                                  background: '#F3F4F6',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px',
                                }}
                              >
                                <FileIcon size={36} color="#6B7280" />
                                <div style={{ fontSize: '10px', color: '#374151', textAlign: 'center', marginTop: '4px', wordBreak: 'break-word' }}>
                                  {media.file.name.length > 15 
                                    ? media.file.name.substring(0, 12) + '...' 
                                    : media.file.name}
                                </div>
                              </div>
                            ) : (
                              <div style={{ position: 'relative' }}>
                                <img
                                  src={media.preview}
                                  alt="Preview"
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    background: '#F3F4F6',
                                  }}
                                />
                                {media.type === 'video' && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '8px',
                                      left: '8px',
                                      background: 'rgba(0, 0, 0, 0.6)',
                                      borderRadius: '20px',
                                      padding: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <PlayCircle size={20} color="white" />
                                  </div>
                                )}
                              </div>
                            )}

                            {isUploading && (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px',
                                }}
                              >
                                <div style={{ fontSize: '12px', color: 'white', fontWeight: '600' }}>
                                  {uploadProgress}%
                                </div>
                                <div style={{ fontSize: '10px', color: 'white', opacity: 0.8 }}>
                                  {uploadProgress < 50 ? 'Compressing...' : 'Uploading...'}
                                </div>
                              </div>
                            )}

                            <button
                              onClick={() => removeMediaFile(index)}
                              disabled={isPosting || isUploading}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                background: 'rgba(239, 68, 68, 0.9)',
                                border: '1px solid white',
                                borderRadius: '12px',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                                opacity: isPosting || isUploading ? 0.3 : 1,
                              }}
                            >
                              <X size={14} color="white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer Actions */}
          {isConnected && (
            <div
              style={{
                padding: '20px 24px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                background: 'white',
              }}
            >
              <button
                onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
                disabled={isPosting || isUploading}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'white',
                  border: '2px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: isPosting || isUploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isPosting || isUploading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  if (!isPosting && !isUploading) e.currentTarget.style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePostTweet}
                disabled={isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit ? '#9CA3AF' : '#000000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: isPosting || isUploading || !tweetText.trim() || characterCount > characterLimit ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!isPosting && !isUploading && tweetText.trim() && characterCount <= characterLimit) {
                    e.currentTarget.style.background = '#0C8DD9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isPosting && !isUploading && tweetText.trim() && characterCount <= characterLimit) {
                    e.currentTarget.style.background = '#000000';
                  }
                }}
              >
                <XIcon size={16} />
                {isUploading ? 'Uploading...' : isPosting ? 'Posting...' : 'Post Tweet'}
              </button>
            </div>
          )}
        </Modal>

      {/* YouTube Video Picker Modal */}
      <YouTubeVideoPickerModal
        isOpen={youtubePickerVisible}
        onClose={() => setYoutubePickerVisible(false)}
        onSelectVideos={handleYouTubeVideosSelected}
        title="Select Videos"
      />
    </>
  );
};

export default BulkTweetCoachModal;
